import type {ReactElement} from 'react';
import {useCallback, useEffect, useId, useMemo, useRef, useState} from 'react';
import {AiChatEmbeddedPresentation} from '@AiChat/ai-chat-embedded.types';
import {MESSAGE_ID_PARAM, getAiChatBasePath, getChatIdFromUrl, getChatUrl, getHistory, isAssetsUrl, isMemoryUrl, isQuickTipsUrl} from '@/ai-chat/lib/chat-history-library';
import {getRecommendedDesignTemplates} from '@Components/ai-chat-window/lib/ai-chat-recommended-templates.library';
import {hasUserSeenAiChatCreateVideoIntroTile} from '@Components/ai-chat-window/components/assistant-message-image-part-action-tiles/assistant-message-image-part-action-tiles.library';
import {PdfLightbox} from '@Components/pdf-lightbox/pdf-lightbox';
import {getAiChatPromptBoxId} from '@Libraries/ai-chat.library';
import {aiChatActionsContext, aiChatStateContext} from './ai-chat.context';
import type {
  AiChatActionsContextType,
  AiChatStateContextType,
  AiChatProviderProps,
  AnimationActionsLoading,
  AnimationActionsLoadingStatesMap,
  AnimationCreationStateMap,
  GeneratedImagePartActionsLoading,
  GeneratedImagePartActionsLoadingStatesMap,
  RecommendedTemplatesMap,
  SetCurrentChatIdOptions,
} from '@/ai-chat/providers/ai-chat-provider.types';
import {AI_CHAT_SIDEBAR_EXPANDED_STATE_COOKIE, DEFAULT_MAKE_TEXT_EDITABLE_LIMIT} from '@/ai-chat/providers/ai-chat-provider.types';
import {mergeIntoNestedStateMap} from '@/ai-chat/providers/nested-state-map.util';
import type {ActionTileStates} from '@Components/ai-chat-window/components/assistant-message-image-part-action-tiles/assistant-message-image-part-action-tiles.types';
import type {RecommendedTemplate} from '@Components/ai-chat-window/components/assistant-message-image-part-action-tiles/components/templates-for-inspiration-tile/templates-for-inspiration-tile.types';
import type {AnimationCreationState} from '@Components/ai-chat-window/components/assistant-message-image-part-animation-creations/assistant-message-image-part-animation-creations.types';
import {AnimationCreationStatus} from '@Components/ai-chat-window/components/assistant-message-image-part-animation-creations/assistant-message-image-part-animation-creations.types';
import type {PromptBoxCoordinatorActions} from '@Components/ai-chat-window/hooks/useAiChatPromptBoxCoordinator';
import {LoadingStates} from '@Utils/loading.util';
import {readCookie, setCookie} from '@Utils/cookie.util';
import {openErrorModal} from '@Modals/error-modal';
import {useGetTeamCredits} from '@Hooks/useGetTeamCredits';
import {getRemainingAiUsages} from '@Libraries/pmw-stock-media-library';
import {isUserLoggedIn} from '@Libraries/user.library';
import {useAiChatImageDownloadEvents} from '@/ai-chat/hooks/useAiChatImageDownloadEvents';
import {useAudioLimit} from '@/ai-chat/hooks/useAudioLimit';
import {useAiChatPreferences} from '@/ai-chat/hooks/useAiChatPreferences';
import {useAiChatSize} from '@AiChat/hooks/useAiChatSize';
import {useUserMemories} from '@/ai-chat/hooks/useUserMemories';
import type {HistoryItem} from '@AiChat/ai-chat.types';
import {AI_CHAT_ASSETS_URL_SEGMENT, AI_CHAT_MEMORY_URL_SEGMENT, AI_CHAT_QUICK_TIPS_URL_SEGMENT} from '@AiChat/ai-chat.types';
import {
  AI_CHAT_GENERATED_ASSET_ADDED_CHANNEL,
  AI_CHAT_GENERATED_ASSET_ADDED_EVENT,
  getShouldShowAssetsIntroSubtitle,
  hasAnyGeneratedAssets,
  recordAssetsIntroSubtitleView,
} from '@AiChat/lib/ai-chat-assets-library';
import {GA4EventName, trackGA4CustomEvent} from '@Libraries/ga-events';
import {fetchMakeTextEditableLimitStatus} from '@Components/ai-chat-window/lib/make-text-editable.library';
import type {MakeTextEditableLimit} from '@Components/ai-chat-window/lib/make-text-editable.library';
import type {ChatMessagePart, ChatOnLoadParams, RequestPreferences} from '@Components/ai-chat-window/ai-chat-window.types';
import {AiChatSource, DecisionsAssist} from '@Components/ai-chat-window/ai-chat-window.types';

const DEFAULT_IMAGE_ACTIONS_LOADING: GeneratedImagePartActionsLoading = {
  downloadLoadingState: LoadingStates.NOT_LOADED,
  shareLoadingState: LoadingStates.NOT_LOADED,
  saveToMyDesignsLoadingState: LoadingStates.NOT_LOADED,
  loadingPublishingOption: null,
  publishingLoadingState: LoadingStates.NOT_LOADED,
  makeTextEditableLoadingState: LoadingStates.NOT_LOADED,
  makeDesignEditableLoadingState: LoadingStates.NOT_LOADED,
  makeTextEditableLayerDecompositionModel: null,
};

const DEFAULT_ANIMATION_ACTIONS_LOADING: AnimationActionsLoading = {
  downloadLoadingState: LoadingStates.NOT_LOADED,
  openEditorLoadingState: LoadingStates.NOT_LOADED,
  loadingPublishingOption: null,
  publishingLoadingState: LoadingStates.NOT_LOADED,
};

const DEFAULT_ANIMATION_CREATION_STATE: AnimationCreationState = {
  isVisible: false,
  status: AnimationCreationStatus.IDLE,
};

function getInitialChatId(props: AiChatProviderProps): string | undefined {
  if (!props.isEmbedded) {
    return getChatIdFromUrl();
  }

  return props.chatId;
}

export function AiChatProvider({children, ...props}: AiChatProviderProps): ReactElement {
  const tracksChatIdInUrl = !props.isEmbedded;
  const instanceId = useId();
  const [currentChatId, setCurrentChatIdInternal] = useState<string | undefined>(getInitialChatId({children, ...props}));
  const [isQuickTipsView, setIsQuickTipsView] = useState<boolean>(tracksChatIdInUrl && isQuickTipsUrl());
  const [isAssetsView, setIsAssetsView] = useState<boolean>(tracksChatIdInUrl && isAssetsUrl());
  const [isMemoryView, setIsMemoryView] = useState<boolean>(tracksChatIdInUrl && isMemoryUrl());
  const [hasGeneratedAssets, setHasGeneratedAssets] = useState<boolean>(props.hasGeneratedAssets);
  const [shouldShowAssetsIntroSubtitle, setShouldShowAssetsIntroSubtitle] = useState<boolean | undefined>(props.shouldShowAssetsIntroSubtitle);
  const [overlayViewReturnUrl, setOverlayViewReturnUrl] = useState<string | undefined>(undefined);
  const [pendingChatOnLoadParams, setPendingChatOnLoadParams] = useState<ChatOnLoadParams | undefined>(undefined);
  const [pdfViewerState, setPdfViewerState] = useState<{url: string; title?: string} | undefined>(undefined);
  const hasMessages = !!currentChatId;
  const isPopupPresentation = props.presentation === AiChatEmbeddedPresentation.POPUP;
  const [hasExpandedPopup, setHasExpandedPopup] = useState<boolean>(false);

  if (isPopupPresentation && currentChatId && !hasExpandedPopup) {
    setHasExpandedPopup(true);
  }

  const isPromptBoxOnly = isPopupPresentation && !currentChatId && !hasExpandedPopup;
  const {isSmall} = useAiChatSize();
  const [isPromptBoxFocused, setIsPromptBoxFocused] = useState<boolean>(false);
  const [chatHistory, setChatHistory] = useState<HistoryItem[]>([]);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [isChatHistorySearchOpen, setIsChatHistorySearchOpen] = useState<boolean>(false);
  const recentChatHistory = chatHistory.slice(0, 5);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const teamCredits = useGetTeamCredits() ?? props.credits;
  const [remainingAiChatUsages, setRemainingAiChatUsages] = useState<number>(teamCredits);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(isUserLoggedIn());
  const [generatedImagePartActionsLoadingStatesMap, setGeneratedImagePartActionsLoadingStatesMap] = useState<GeneratedImagePartActionsLoadingStatesMap>({});
  const defaultActionTileStates: ActionTileStates = {
    isTemplatesForInspirationVisible: true,
    isCreateAVideoFeatureIntroVisible: false,
  };
  const [actionTileStates, setActionTileStates] = useState<ActionTileStates>(defaultActionTileStates);
  const [recommendedTemplatesMap, setRecommendedTemplatesMap] = useState<RecommendedTemplatesMap>({});
  const [animationCreationStateMap, setAnimationCreationStateMap] = useState<AnimationCreationStateMap>({});
  const [animationActionsLoadingStatesMap, setAnimationActionsLoadingStatesMap] = useState<AnimationActionsLoadingStatesMap>({});
  const [isPaygUserWithAiChatAccess, setIsPaygUserWithAiChatAccess] = useState<boolean>(props.isPaygUserWithAiChatAccess);
  const [isReturningUser, setIsReturningUser] = useState<boolean>(props.isReturningUser);
  const [makeTextEditableLimit, setMakeTextEditableLimit] = useState<MakeTextEditableLimit>(props.makeTextEditableLimit ?? DEFAULT_MAKE_TEXT_EDITABLE_LIMIT);
  const [editingUserMessageIdMap, setEditingUserMessageIdMap] = useState<Record<string, string | null>>({});
  const [showDeletedMessages, setShowDeletedMessages] = useState<boolean>(false);
  const [hideGenerationContext, setHideGenerationContext] = useState<boolean>(true);
  const [decisionsAssist, setDecisionsAssist] = useState<DecisionsAssist>(DecisionsAssist.ON);
  const [isChatHistorySearchLoading, setIsChatHistorySearchLoading] = useState<boolean>(false);
  const [landingTipRefreshKey, setNewChatRefreshKey] = useState<number>(0);
  const disconnectChatStreamRef = useRef<((chatId: string) => void) | null>(null);
  const promptBoxCoordinatorRef = useRef<PromptBoxCoordinatorActions | null>(null);
  const userHasChats = props.isReturningUser;
  const opensWithAnExpandedSidebar = !props.isEmbedded && !isSmall && userHasChats && readCookie(AI_CHAT_SIDEBAR_EXPANDED_STATE_COOKIE) !== '0';
  const [isSidebarExpanded, setIsSidebarExpandedInternal] = useState<boolean>(opensWithAnExpandedSidebar);
  const [isCreditsViewOpen, setIsCreditsViewOpen] = useState<boolean>(false);

  const shouldRememberSidebarPreference = !props.isEmbedded && !isSmall && isReturningUser;
  const setIsSidebarExpanded = useCallback(
    (expanded: boolean): void => {
      setIsSidebarExpandedInternal(expanded);

      if (!expanded) {
        setIsCreditsViewOpen(false);
      }

      if (shouldRememberSidebarPreference) {
        setCookie(AI_CHAT_SIDEBAR_EXPANDED_STATE_COOKIE, expanded ? '1' : '0', 365);
      }
    },
    [shouldRememberSidebarPreference]
  );

  const openCreditsView = useCallback((): void => {
    setIsCreditsViewOpen(true);
  }, []);

  const closeCreditsView = useCallback((): void => {
    setIsCreditsViewOpen(false);
  }, []);

  const disconnectChatStream = useCallback((chatId: string): void => {
    disconnectChatStreamRef.current?.(chatId);
  }, []);

  const registerDisconnectChatStream = useCallback((disconnectFn: (chatId: string) => void): void => {
    disconnectChatStreamRef.current = disconnectFn;
  }, []);
  const {fetchStatus: fetchAudioLimitStatus} = useAudioLimit();

  const activateForNewChat = useCallback((): void => {
    setNewChatRefreshKey((prev) => prev + 1);
    promptBoxCoordinatorRef.current?.activateForNewChat();
  }, []);

  const activatePromptBox = useCallback((): void => {
    promptBoxCoordinatorRef.current?.activatePromptBox();
  }, []);

  const openChatHistorySearch = useCallback((): void => {
    setIsChatHistorySearchOpen(true);
  }, []);

  const closeChatHistorySearch = useCallback((): void => {
    setIsChatHistorySearchOpen(false);
  }, []);

  const registerPromptBoxCoordinator = useCallback((actions: PromptBoxCoordinatorActions | null): void => {
    promptBoxCoordinatorRef.current = actions;
  }, []);

  const {preferences, hasSeenTemplatesTile, hideActionTilesPermanently, setGenerateAudio: setAiChatGenerateAudio, markTemplatesTileAsSeen} = useAiChatPreferences(isLoggedIn);
  const {memories: userMemories, loadState: userMemoriesLoadState, forgetFact: forgetMemoryFact, forgetVisualEntity: forgetMemoryVisualEntity} = useUserMemories(isMemoryView);
  const [searchTagsByChatId, setSearchTagsByChatId] = useState<Record<string, string[]>>({});

  const setSearchTagsForChat = useCallback((chatId: string, tags: string[]): void => {
    if (tags.length === 0) {
      return;
    }
    setSearchTagsByChatId((prev) => {
      const existing = prev[chatId];
      if (existing?.length === tags.length && existing.every((t, i) => t === tags[i])) {
        return prev;
      }
      return {...prev, [chatId]: tags};
    });
  }, []);

  const getLoadingStatesForImageActions = (chatId: string, imageHashedId: string): GeneratedImagePartActionsLoading => {
    return generatedImagePartActionsLoadingStatesMap[chatId]?.[imageHashedId] ?? DEFAULT_IMAGE_ACTIONS_LOADING;
  };

  const updateLoadingStatesForImageActions = useCallback((chatId: string, imageHashedId: string, updatedStates: Partial<GeneratedImagePartActionsLoading>): void => {
    mergeIntoNestedStateMap(setGeneratedImagePartActionsLoadingStatesMap, chatId, imageHashedId, DEFAULT_IMAGE_ACTIONS_LOADING, updatedStates);
  }, []);

  const getLoadingStatesForAnimationActions = (chatId: string, animationHashedId: string): AnimationActionsLoading => {
    return animationActionsLoadingStatesMap[chatId]?.[animationHashedId] ?? DEFAULT_ANIMATION_ACTIONS_LOADING;
  };

  const updateLoadingStatesForAnimationActions = useCallback((chatId: string, animationHashedId: string, updatedStates: Partial<AnimationActionsLoading>): void => {
    mergeIntoNestedStateMap(setAnimationActionsLoadingStatesMap, chatId, animationHashedId, DEFAULT_ANIMATION_ACTIONS_LOADING, updatedStates);
  }, []);

  const getActionTileStates = (): ActionTileStates => {
    return actionTileStates;
  };

  const updateActionTileStates = useCallback((updatedStates: Partial<ActionTileStates>): void => {
    setActionTileStates((prev) => ({...prev, ...updatedStates}));
  }, []);

  const getRecommendedTemplates = (chatId: string): RecommendedTemplate[] => {
    return recommendedTemplatesMap[chatId]?.templates ?? [];
  };

  const getTemplateSearchTerm = (chatId: string): string | null | undefined => {
    return recommendedTemplatesMap[chatId]?.templateSearchTerm;
  };

  const fetchRecommendedTemplates = useCallback(
    (chatId: string, searchTags: string[]): void => {
      if (preferences.areActionTilesHidden) {
        return;
      }

      setRecommendedTemplatesMap((prev) => ({...prev, [chatId]: {templates: [], templateSearchTerm: null}}));

      void (async (): Promise<void> => {
        try {
          const response = await getRecommendedDesignTemplates(searchTags);
          setRecommendedTemplatesMap((prev) => ({...prev, [chatId]: response}));
        } catch (e: unknown) {
          console.error('Failed to fetch recommended templates:', e);
          setRecommendedTemplatesMap((prev) => {
            const {[chatId]: _discarded, ...rest} = prev;
            return rest;
          });
        }
      })();
    },
    [preferences.areActionTilesHidden]
  );

  const currentChatTags = currentChatId ? searchTagsByChatId[currentChatId] : undefined;

  useEffect(() => {
    if (currentChatId && currentChatTags && currentChatTags.length > 0) {
      fetchRecommendedTemplates(currentChatId, currentChatTags);
    }
  }, [currentChatId, currentChatTags, fetchRecommendedTemplates]);

  const getAnimationCreationState = (chatId: string, imageHashedId: string): AnimationCreationState => {
    return animationCreationStateMap[chatId]?.[imageHashedId] ?? DEFAULT_ANIMATION_CREATION_STATE;
  };

  const updateAnimationCreationState = useCallback((chatId: string, imageHashedId: string, updatedState: Partial<AnimationCreationState>): void => {
    mergeIntoNestedStateMap(setAnimationCreationStateMap, chatId, imageHashedId, DEFAULT_ANIMATION_CREATION_STATE, updatedState);
  }, []);

  const fetchChatHistory = useCallback(
    async (search: string | undefined, offset = 0, limit = 20): Promise<HistoryItem[]> => {
      if (!isLoggedIn) {
        return [];
      }

      if (search !== undefined && offset === 0) {
        setIsChatHistorySearchLoading(true);
      }

      try {
        const newItems = await getHistory(search, offset, limit);

        if (offset === 0) {
          setChatHistory(newItems);
        } else {
          setChatHistory((prev) => [...prev, ...newItems]);
        }

        if (newItems.length > 0) {
          setIsReturningUser(true);
        }

        if (newItems.length < limit) {
          setHasMoreHistory(false);
        }

        return newItems;
      } catch (e) {
        console.error(e);
        openErrorModal();
        return [];
      } finally {
        if (search !== undefined && offset === 0) {
          setIsChatHistorySearchLoading(false);
        }
      }
    },
    [setChatHistory, setHasMoreHistory, openErrorModal, isLoggedIn]
  );

  const refreshRemainingAiChatInvocations = useCallback((): void => {
    getRemainingAiUsages()
      .then((res) => {
        setRemainingAiChatUsages(res);
      })
      .catch((e: unknown) => {
        console.error(e);
      });
  }, []);

  const refreshMakeTextEditableLimit = useCallback(async (): Promise<void> => {
    try {
      setMakeTextEditableLimit(await fetchMakeTextEditableLimitStatus());
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    refreshRemainingAiChatInvocations();
  }, []);

  const refreshCreateVideoIntroTileVisibility = useCallback((): void => {
    void (async (): Promise<void> => {
      const hasSeen = await hasUserSeenAiChatCreateVideoIntroTile();
      if (!hasSeen) {
        setActionTileStates((prev) => ({...prev, isCreateAVideoFeatureIntroVisible: true}));
      }
    })();
  }, []);

  useEffect(() => {
    refreshCreateVideoIntroTileVisibility();
  }, [refreshCreateVideoIntroTileVisibility]);

  useAiChatImageDownloadEvents({
    updateLoadingStatesForImageActions,
    refreshCredits: refreshRemainingAiChatInvocations,
  });

  const refetchChatHistory = useCallback(async (): Promise<void> => {
    setSearchQuery(undefined);

    await fetchChatHistory(undefined, 0);
  }, [fetchChatHistory]);

  const syncStateFromUrl = useCallback((): void => {
    if (!tracksChatIdInUrl) {
      return;
    }

    setIsQuickTipsView(isQuickTipsUrl());
    setIsAssetsView(isAssetsUrl());
    setIsMemoryView(isMemoryUrl());
    setCurrentChatIdInternal(getChatIdFromUrl());
  }, [tracksChatIdInUrl]);

  const promptBoxId = getAiChatPromptBoxId(instanceId, currentChatId);

  /** An embedded chat owns no url, so its overlay views live in state alone. */
  const showOverlayViewLocally = useCallback((urlSegment: string | undefined): void => {
    setIsQuickTipsView(urlSegment === AI_CHAT_QUICK_TIPS_URL_SEGMENT);
    setIsAssetsView(urlSegment === AI_CHAT_ASSETS_URL_SEGMENT);
    setIsMemoryView(urlSegment === AI_CHAT_MEMORY_URL_SEGMENT);
  }, []);

  const setCurrentChatId = useCallback((newChatId: string | undefined, options?: SetCurrentChatIdOptions): void => {
    if (!tracksChatIdInUrl) {
      setCurrentChatIdInternal(newChatId);
      showOverlayViewLocally(undefined);

      return;
    }

    if (newChatId === getChatIdFromUrl() && !isQuickTipsUrl() && !isAssetsUrl() && !isMemoryUrl()) {
      return;
    }

    let newUrl = getAiChatBasePath();
    if (newChatId) {
      newUrl = getChatUrl(newChatId);
    }

    if (options?.messageId) {
      newUrl = `${newUrl}?${MESSAGE_ID_PARAM}=${encodeURIComponent(options.messageId)}`;
    }

    if (options?.replaceHistory) {
      window.history.replaceState({path: newUrl}, '', newUrl);
    } else {
      window.history.pushState({path: newUrl}, '', newUrl);
    }
    syncStateFromUrl();
  }, [showOverlayViewLocally, syncStateFromUrl, tracksChatIdInUrl]);

  /** First entry wins, so hopping overlay to overlay still returns to the chat underneath. */
  const navigateToOverlayView = useCallback(
    (urlSegment: string): void => {
      if (!tracksChatIdInUrl) {
        showOverlayViewLocally(urlSegment);

        return;
      }

      const overlayUrl = `${getAiChatBasePath()}/${urlSegment}`;
      if (!isQuickTipsUrl() && !isMemoryUrl()) {
        setOverlayViewReturnUrl(window.location.pathname);
      }
      window.history.replaceState({path: overlayUrl}, '', overlayUrl);
      syncStateFromUrl();
    },
    [showOverlayViewLocally, syncStateFromUrl, tracksChatIdInUrl]
  );

  const startNewChat = useCallback((): void => {
    activateForNewChat();
    setCurrentChatId(undefined);
    setIsSidebarExpanded(false);
  }, [activateForNewChat, setCurrentChatId, setIsSidebarExpanded]);

  const navigateToQuickTips = useCallback((): void => {
    navigateToOverlayView(AI_CHAT_QUICK_TIPS_URL_SEGMENT);
    trackGA4CustomEvent(GA4EventName.AICHAT_QUICK_TIPS);
  }, [navigateToOverlayView]);

  const navigateToMemory = useCallback((): void => {
    navigateToOverlayView(AI_CHAT_MEMORY_URL_SEGMENT);
    trackGA4CustomEvent(GA4EventName.AICHAT_MEMORY_OPEN);
  }, [navigateToOverlayView]);

  const navigateBack = useCallback((): void => {
    if (!tracksChatIdInUrl) {
      showOverlayViewLocally(undefined);

      return;
    }

    const returnUrl = overlayViewReturnUrl ?? getAiChatBasePath();
    window.history.replaceState({path: returnUrl}, '', returnUrl);
    setOverlayViewReturnUrl(undefined);
    syncStateFromUrl();
  }, [overlayViewReturnUrl, showOverlayViewLocally, syncStateFromUrl, tracksChatIdInUrl]);

  const navigateToAssets = useCallback((): void => {
    trackGA4CustomEvent(GA4EventName.AICHAT_MY_AI_DESIGNS);

    if (!tracksChatIdInUrl) {
      showOverlayViewLocally(AI_CHAT_ASSETS_URL_SEGMENT);

      return;
    }

    if (isAssetsUrl()) {
      return;
    }
    const assetsUrl = `${getAiChatBasePath()}/${AI_CHAT_ASSETS_URL_SEGMENT}`;
    window.history.pushState({path: assetsUrl}, '', assetsUrl);
    syncStateFromUrl();
  }, [showOverlayViewLocally, syncStateFromUrl, tracksChatIdInUrl]);

  const clearPendingChatOnLoadParams = useCallback((): void => {
    setPendingChatOnLoadParams(undefined);
  }, []);

  const startNewChatWithParts = useCallback((parts: ChatMessagePart[], preferences?: RequestPreferences): void => {
    setPendingChatOnLoadParams({
      userRequestParts: parts,
      source: AiChatSource.AI_CHAT_PAGE,
      sendOnLoad: true,
      replaceHistoryOnChatCreation: true,
      preferences,
    });
    setCurrentChatId(undefined);
  }, [setCurrentChatId]);

  useEffect(() => {
    const handleUrlChange = (): void => {
      syncStateFromUrl();
    };

    window.addEventListener('popstate', handleUrlChange);

    handleUrlChange();

    const handlePageShow = (event: PageTransitionEvent): void => {
      if (event.persisted) {
        handleUrlChange();
        void refetchChatHistory();
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return (): void => {
      window.removeEventListener('popstate', handleUrlChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [currentChatId]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    void refetchChatHistory();
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn || shouldShowAssetsIntroSubtitle !== undefined) {
      return;
    }

    void getShouldShowAssetsIntroSubtitle()
      .then(setShouldShowAssetsIntroSubtitle)
      .catch((e: unknown) => {
        console.error('Failed to fetch assets intro subtitle flag:', e);
      });
  }, [isLoggedIn, shouldShowAssetsIntroSubtitle]);

  useEffect(() => {
    if (hasGeneratedAssets) {
      return;
    }

    const revealGeneratedAssetsEntry = (): void => {
      setHasGeneratedAssets(true);
    };

    window.addEventListener(AI_CHAT_GENERATED_ASSET_ADDED_EVENT, revealGeneratedAssetsEntry);

    const generatedAssetChannel = new BroadcastChannel(AI_CHAT_GENERATED_ASSET_ADDED_CHANNEL);
    generatedAssetChannel.addEventListener('message', revealGeneratedAssetsEntry);

    return (): void => {
      window.removeEventListener(AI_CHAT_GENERATED_ASSET_ADDED_EVENT, revealGeneratedAssetsEntry);
      generatedAssetChannel.removeEventListener('message', revealGeneratedAssetsEntry);
      generatedAssetChannel.close();
    };
  }, [hasGeneratedAssets]);

  const onLoginSuccessActions = useCallback((): void => {
    setIsLoggedIn(isUserLoggedIn());
    refreshRemainingAiChatInvocations();
    void fetchAudioLimitStatus();
    refreshCreateVideoIntroTileVisibility();
    void hasAnyGeneratedAssets()
      .then(setHasGeneratedAssets)
      .catch((e: unknown) => {
        console.error(e);
      });
  }, [refreshRemainingAiChatInvocations, fetchAudioLimitStatus, refreshCreateVideoIntroTileVisibility]);

  const updateChatHistoryItem = useCallback((chatId: string, updates: Partial<HistoryItem>): void => {
    setChatHistory((prev) =>
      prev.map((item) => {
        if (item.idAiChat === chatId) {
          return {...item, ...updates};
        }

        return item;
      })
    );
  }, []);

  const deleteChatHistoryItem = useCallback((chatId: string): void => {
    setChatHistory((prev) => prev.filter((item) => item.idAiChat !== chatId));
  }, []);

  const setEditingUserMessageId = useCallback(
    (messageId: string | null, chatId?: string): void => {
      const idToUpdate = chatId ?? currentChatId;
      if (!idToUpdate) {
        return;
      }

      setEditingUserMessageIdMap((prev) => ({
        ...prev,
        [idToUpdate]: messageId,
      }));
    },
    [currentChatId]
  );

  useEffect(() => {
    setPdfViewerState(undefined);
  }, [currentChatId]);

  const openPdf = useCallback(
    (url: string, title?: string): void => {
      setIsSidebarExpanded(false);
      setPdfViewerState({url, title});
    },
    [setIsSidebarExpanded]
  );

  const consumeAssetsIntroSubtitleView = useCallback((): void => {
    void recordAssetsIntroSubtitleView()
      .then((shouldShow) => {
        setShouldShowAssetsIntroSubtitle(shouldShow);
      })
      .catch((e: unknown) => {
        console.error('Failed to increment assets intro subtitle seen count:', e);
      });
  }, []);

  const closePdf = useCallback((): void => {
    setPdfViewerState(undefined);
  }, []);

  const stateValue: AiChatStateContextType = {
    hasMessages,
    currentChatId: currentChatId,
    isSidebarExpanded: isSidebarExpanded,
    isCreditsViewOpen,
    recentChatHistory,
    chatHistory,
    hasMoreHistory,
    isPromptBoxFocused,
    searchQuery,
    isChatHistorySearchOpen,
    credits: remainingAiChatUsages,
    isUserLoggedIn: isLoggedIn,
    isSuperUser: props.isSuperUser,
    canUseEditableTextLayers: props.canUseEditableTextLayers,
    isMakeDesignEditableEnabled: props.isMakeDesignEditableEnabled,
    makeDesignEditableCreditCost: props.makeDesignEditableCreditCost,
    chargeForMakeTextEditable: props.chargeForMakeTextEditable,
    makeTextEditableLimit,
    showDeletedMessages,
    hideGenerationContext,
    decisionsAssist,
    getLoadingStatesForImageActions,
    getLoadingStatesForAnimationActions,
    getActionTileStates,
    getRecommendedTemplates,
    getTemplateSearchTerm,
    getAnimationCreationState,
    aiChatGenerateAudio: preferences.generateAudio,
    isPaygUserWithAiChatAccess,
    editingUserMessageIdMap,
    isReturningUser,
    areActionTilesHidden: preferences.areActionTilesHidden,
    hasSeenTemplatesTile,
    isTemplateRedesignFeatureEnabled: props.isTemplateRedesignFeatureEnabled,
    previewSeason: props.previewSeason,
    christmasGiftOffer: props.christmasGiftOffer,
    promptBoxPlaceholderKey: props.promptBoxPlaceholderKey,
    isAiChatCreateAVideoFeatureEnabled: props.isAiChatCreateAVideoFeatureEnabled,
    landingTipRefreshKey,
    isQuickTipsView,
    isAssetsView,
    isMemoryView,
    isMemoryEnabled: props.isMemoryEnabled,
    capabilityGroups: props.capabilityGroups,
    userMemories,
    userMemoriesLoadState,
    hasGeneratedAssets,
    shouldShowAssetsIntroSubtitle,
    isChatHistorySearchLoading: isChatHistorySearchLoading,
    pendingChatOnLoadParams,
    landingSource: props.landingSource,
    promptBoxId,
    isEmbedded: props.isEmbedded,
    isPopupPresentation,
    isPromptBoxOnly,
  };

  const actionsValue: AiChatActionsContextType = useMemo(
    () => ({
      setCurrentChatId,
      setCurrentChatIdInternal,
      setIsSidebarExpanded,
      openCreditsView,
      closeCreditsView,
      refetchChatHistory,
      fetchChatHistory,
      setIsPromptBoxFocused,
      setSearchQuery,
      openChatHistorySearch,
      closeChatHistorySearch,
      refreshCredits: refreshRemainingAiChatInvocations,
      refreshMakeTextEditableLimit,
      onLoginSuccessActions,
      updateLoadingStatesForImageActions,
      updateLoadingStatesForAnimationActions,
      updateActionTileStates,
      updateAnimationCreationState,
      setAiChatGenerateAudio,
      updateChatHistoryItem,
      disconnectChatStream,
      registerDisconnectChatStream,
      deleteChatHistoryItem,
      setEditingUserMessageId,
      setShowDeletedMessages,
      setHideGenerationContext,
      setDecisionsAssist,
      setIsPaygUserWithAiChatAccess,
      activateForNewChat,
      startNewChat,
      activatePromptBox,
      registerPromptBoxCoordinator,
      navigateToQuickTips,
      navigateToMemory,
      forgetMemoryFact,
      forgetMemoryVisualEntity,
      navigateBack,
      navigateToAssets,
      startNewChatWithParts,
      clearPendingChatOnLoadParams,
      hideActionTilesPermanently,
      setSearchTagsForChat,
      markTemplatesTileAsSeen,
      consumeAssetsIntroSubtitleView,
      openPdf,
    }),
    [
      setCurrentChatId,
      setCurrentChatIdInternal,
      setIsSidebarExpanded,
      openCreditsView,
      closeCreditsView,
      refetchChatHistory,
      fetchChatHistory,
      setIsPromptBoxFocused,
      setSearchQuery,
      openChatHistorySearch,
      closeChatHistorySearch,
      refreshRemainingAiChatInvocations,
      refreshMakeTextEditableLimit,
      onLoginSuccessActions,
      updateLoadingStatesForImageActions,
      updateLoadingStatesForAnimationActions,
      updateActionTileStates,
      updateAnimationCreationState,
      setAiChatGenerateAudio,
      updateChatHistoryItem,
      disconnectChatStream,
      registerDisconnectChatStream,
      deleteChatHistoryItem,
      setEditingUserMessageId,
      setShowDeletedMessages,
      setHideGenerationContext,
      setDecisionsAssist,
      setIsPaygUserWithAiChatAccess,
      activateForNewChat,
      startNewChat,
      activatePromptBox,
      registerPromptBoxCoordinator,
      navigateToQuickTips,
      navigateToMemory,
      forgetMemoryFact,
      forgetMemoryVisualEntity,
      navigateBack,
      navigateToAssets,
      startNewChatWithParts,
      clearPendingChatOnLoadParams,
      hideActionTilesPermanently,
      setSearchTagsForChat,
      markTemplatesTileAsSeen,
      consumeAssetsIntroSubtitleView,
      openPdf,
    ]
  );

  return (
    <aiChatStateContext.Provider value={stateValue}>
      <aiChatActionsContext.Provider value={actionsValue}>
        {children}
        <PdfLightbox isOpen={pdfViewerState !== undefined} url={pdfViewerState?.url} title={pdfViewerState?.title} chatId={currentChatId} onClose={closePdf} />
      </aiChatActionsContext.Provider>
    </aiChatStateContext.Provider>
  );
}
